import React, { useContext } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { buildLink, getPath } from "common/routing";
import {
  GetWorkflowDefinitionQuery,
  useGetWorkflowDefinitionQuery,
} from "../../../generated/graphql";
import { setTitle } from "../../../utils/title";
import { getCurrentURL } from "../../../utils/url";
import { usePublicUserAuthModal } from "../../Authentication/Public/PublicUserAuthModal";
import { AuthContext } from "../../Authorization/AuthContext";
import { Markdown } from "../../Common/__styles__/Markdown";
import { ButtonLink } from "../../Common/ButtonLink";
import { Button } from "../../Common/Buttons/Button";
import { Title } from "../../Common/Typography";
import { Loading } from "../__styles__/Guest";
import AccessDeniedPage, { ACCESS_DENIED_RESOURCE } from "../AccessDeniedPage";
import Layout from "../Layout";
import { accountTitle } from "../utils";
import { WorkflowsPageContainer } from "./__styles__/WorkflowsPageContainer";

export const WorkflowDefinition = () => {
  const history = useHistory();
  const location = useLocation<{ prevLocation?: string }>();
  const { publicUser } = useContext(AuthContext);
  const { workflowDefinitionId } = useParams<{
    workflowDefinitionId: string;
  }>();

  const { data, loading } = useGetWorkflowDefinitionQuery({
    variables: {
      workflowDefinitionId,
      isGuest: !publicUser,
    },
    fetchPolicy: "network-only",
  });

  const title = data?.workflowDefinition?.name ?? "Loading...";
  setTitle(`${title} | ${accountTitle()}`);

  if (loading) {
    return <Loading />;
  }

  if (!data?.workflowDefinition) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.WORKFLOW} />;
  }

  const prevLocation = location.state?.prevLocation ?? getPath("guestHome");

  return (
    <Layout hasGutters={true}>
      <WorkflowsPageContainer>
        <ButtonLink
          styleVariant="primary"
          size="medium"
          leftIconName="chevron-left"
          onClick={() => history.push(prevLocation)}
          style={{ width: "fit-content" }}
        >
          Back
        </ButtonLink>
        <Title size="large" type="semiBold">
          {data.workflowDefinition.name}
        </Title>

        {data.workflowDefinition.description && (
          <Markdown markdown={data.workflowDefinition.description} />
        )}
        <div>
          <CallToAction
            workflowDefinition={data.workflowDefinition}
            workflows={data.workflows ?? []}
          />
        </div>
      </WorkflowsPageContainer>
    </Layout>
  );
};

const CallToAction = ({
  workflowDefinition,
  workflows,
}: {
  workflowDefinition: NonNullable<
    GetWorkflowDefinitionQuery["workflowDefinition"]
  >;
  workflows: NonNullable<GetWorkflowDefinitionQuery["workflows"]>;
}) => {
  const { publicUser } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();

  const callToActionDisplay =
    workflows?.length ? "View workflows" : workflowDefinition.callToAction;

  const [showPublicUserLoginModal] = usePublicUserAuthModal();

  const canCreateGroups = workflowDefinition.userGroups.map(({ id }) => id);
  let onCallToActionClick: () => void;
  if (!publicUser) {
    onCallToActionClick = showPublicUserLoginModal;
  } else if (workflows?.length) {
    onCallToActionClick = () => {
      history.push(
        buildLink("listWorkflows", {
          workflowDefinitionId: workflowDefinition.id,
        }),
        { prevLocation: getCurrentURL(location) },
      );
    };
  } else if (
    publicUser.userGroups.some(({ id }) => canCreateGroups.includes(id))
  ) {
    onCallToActionClick = () => {
      history.push(
        buildLink("createSubmission", {
          submissionTypeId: workflowDefinition.initialSubmissionTypeId,
        }),
        { prevLocation: getCurrentURL(location) },
      );
    };
  } else {
    return null;
  }

  return (
    <Button
      variant="primary"
      size="medium"
      onPress={onCallToActionClick}
      title={callToActionDisplay}
    />
  );
};
